import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
} from "@mui/x-data-grid";
import { itIT } from "@mui/x-data-grid/locales";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/Add";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAuth } from "../contexts/AuthContext";
import { ConfirmDialog } from "../components/common";
import {
  PortMapping,
  createPortMapping,
  deletePortMapping,
  listPortMappings,
  updatePortMapping,
} from "../services/api";

const QUERY_KEY = "port-mappings";

type FormState = {
  code: string;
  name: string;
  kind: "PORTO" | "AEREO";
  country_code: string;
};

const EMPTY_FORM: FormState = { code: "", name: "", kind: "AEREO", country_code: "" };

export const PortMappingsSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const hasPermission = user?.role === "admin" || user?.role === "mantainer";

  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [kindFilter, setKindFilter] = React.useState("");
  const [countryFilter, setCountryFilter] = React.useState("");
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  React.useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  React.useEffect(() => {
    setPaginationModel((p) => ({ ...p, page: 0 }));
  }, [search, kindFilter, countryFilter]);

  const queryParams = {
    q: search || undefined,
    kind: kindFilter || undefined,
    country: countryFilter || undefined,
    page: paginationModel.page,
    page_size: paginationModel.pageSize,
  };

  const { data, isLoading, isFetching, error } = useQuery(
    [QUERY_KEY, queryParams],
    () => listPortMappings(queryParams),
    { keepPreviousData: true, enabled: hasPermission }
  );

  const [formOpen, setFormOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = React.useState<PortMapping | null>(null);

  const createMutation = useMutation(createPortMapping, {
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY);
      toast.success("Mappatura creata con successo");
      setFormOpen(false);
    },
        onError: (err: any) => {
          toast.error(err?.response?.data?.detail || "Creazione non riuscita");
        },
  });

  const updateMutation = useMutation(
    (payload: { id: string; data: Partial<FormState> }) => updatePortMapping(payload.id, payload.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEY);
        toast.success("Mappatura aggiornata con successo");
        setFormOpen(false);
      },
        onError: (err: any) => {
          toast.error(err?.response?.data?.detail || "Aggiornamento non riuscito");
        },
    }
  );

  const deleteMutation = useMutation(deletePortMapping, {
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY);
      toast.success("Mappatura eliminata con successo");
      setDeleteTarget(null);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || "Eliminazione non riuscita");
      setDeleteTarget(null);
    },
  });

  if (!hasPermission) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Non hai i permessi per accedere a questa sezione. Solo gli
          amministratori e i mantainer possono gestire le mappature.
        </Alert>
      </Box>
    );
  }

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const handleOpenEdit = (row: PortMapping) => {
    setEditingId(row.id);
    setForm({
      code: row.code,
      name: row.name,
      kind: row.kind,
      country_code: row.country_code || "",
    });
    setFormOpen(true);
  };

  const handleSave = () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast.error("Codice e nome sono obbligatori");
      return;
    }
    const payload = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      kind: form.kind,
      country_code: form.country_code.trim().toUpperCase() || undefined,
    };
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload as any);
    }
  };

  const columns: GridColDef[] = [
    { field: "code", headerName: "Codice", width: 100 },
    { field: "name", headerName: "Nome naturale", width: 260 },
    {
      field: "kind", headerName: "Tipo", width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === "AEREO" ? "Aeroporto" : "Porto"}
          size="small"
          color={params.value === "AEREO" ? "info" : "primary"}
          variant="outlined"
        />
      ),
    },
    { field: "country_code", headerName: "Paese", width: 90 },
    {
      field: "source", headerName: "Origine", width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value === "CUSTOM" ? "Personalizzato" : "Seed"}
          size="small"
          variant={params.value === "CUSTOM" ? "filled" : "outlined"}
        />
      ),
    },
    {
      field: "actions", type: "actions", headerName: "Azioni", width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Modifica"
          onClick={() => handleOpenEdit(params.row as PortMapping)}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label="Elimina"
          onClick={() => setDeleteTarget(params.row as PortMapping)}
        />,
      ],
    },
  ];

  const rows = (data?.items || []).map((r) => ({ ...r }));

  return (
    <Box sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 2, flexShrink: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 1000 }}>
          Archivio dei codici (UN/LOCODE) e dei
          rispettivi nomi naturali, usato dal tool Distanze per riconoscere
          automaticamente i porti/aeroporti scritti come testo libero nei file
          Excel caricati. Aggiungi qui nuovi alias/varianti se il sistema non
          riconosce un nome specifico usato dai tuoi documenti.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate} sx={{ flexShrink: 0 }}>
          Nuova mappatura
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", flexShrink: 0 }}>
        <TextField
          size="small"
          placeholder="Cerca per nome o codice…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Tipo</InputLabel>
          <Select label="Tipo" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
            <MenuItem value="">Tutti</MenuItem>
            <MenuItem value="PORTO">Porto</MenuItem>
            <MenuItem value="AEREO">Aeroporto</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Paese (ISO2)"
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value.toUpperCase())}
          sx={{ width: 130 }}
          inputProps={{ maxLength: 2 }}
        />
      </Box>

      {Boolean(error) && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          Errore nel caricamento dei dati.
        </Alert>
      )}

      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading || isFetching}
          paginationMode="server"
          rowCount={data?.total || 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          sx={{ borderRadius: 2 }}
          localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        />
      </Box>

      {/* Dialog crea/modifica */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? "Modifica mappatura" : "Nuova mappatura"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={form.kind}
                onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as "PORTO" | "AEREO" }))}
              >
                <MenuItem value="AEREO">Aeroporto (codice IATA, 3 lettere)</MenuItem>
                <MenuItem value="PORTO">Porto (codice UN/LOCODE, 5 caratteri)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Codice"
              size="small"
              fullWidth
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              helperText={form.kind === "AEREO" ? "Es. FCO" : "Es. ITVCE"}
            />
            <TextField
              label="Nome naturale"
              size="small"
              fullWidth
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              helperText="Il nome/alias che vuoi far riconoscere (es. 'Aeroporto di Fiumicino')"
            />
            <TextField
              label="Paese (ISO2, opzionale)"
              size="small"
              value={form.country_code}
              onChange={(e) => setForm((f) => ({ ...f, country_code: e.target.value.toUpperCase() }))}
              inputProps={{ maxLength: 2 }}
              sx={{ width: 160 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFormOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={createMutation.isLoading || updateMutation.isLoading}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        loading={deleteMutation.isLoading}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Conferma eliminazione"
        content={`Eliminare la mappatura "${deleteTarget?.name}" (${deleteTarget?.code})?`}
        confirmText="Elimina"
        cancelText="Annulla"
      />
    </Box>
  );
};
